import { selectionConstants, initialSelectionState } from '../_constants';

// Keeps the ordered list and the membership index in step. Every change goes
// through here, so the two can never disagree.
function withIds(state, ids) {
  const seen = {};
  const ordered = [];
  for (const id of ids) {
    if (id == null || seen[id]) continue;
    seen[id] = true;
    ordered.push(id);
  }
  return { ...state, ids: ordered, byId: seen };
}

// Everything between the anchor and `id`, inclusive, in the order the user
// sees the stack. Shift-click is additive, as in a file manager: it extends
// the selection rather than replacing it.
function range(state, id, orderedIds) {
  const to = orderedIds.indexOf(id);
  if (to === -1) return state;
  const from = state.anchor == null ? -1 : orderedIds.indexOf(state.anchor);
  // No usable anchor -- first click, or the anchor scrolled out of the
  // filtered view -- so a shift-click selects just the one scan.
  if (from === -1) {
    return { ...withIds(state, [...state.ids, id]), anchor: id };
  }
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  const span = orderedIds.slice(lo, hi + 1);
  // The anchor stays where it was, so a second shift-click re-extends from
  // the same origin instead of from wherever the last range ended.
  return withIds(state, [...state.ids, ...span]);
}

export function selection(state = initialSelectionState, action) {
  switch (action.type) {
    case selectionConstants.ENTER:
      return { ...state, active: true };

    case selectionConstants.EXIT:
      // Leaving selection mode drops the set but keeps the undo, so a user
      // who tags, exits and then thinks better of it can still take it back.
      return { ...initialSelectionState, undo: state.undo };

    case selectionConstants.TOGGLE: {
      const { id } = action;
      if (id == null) return state;
      const next = state.byId[id]
        ? state.ids.filter((x) => x !== id)
        : [...state.ids, id];
      return { ...withIds(state, next), anchor: id };
    }

    case selectionConstants.RANGE:
      return range(state, action.id, action.orderedIds || []);

    case selectionConstants.SET:
      return { ...withIds(state, action.ids || []), anchor: null };

    case selectionConstants.CLEAR:
      return { ...withIds(state, []), anchor: null };

    // Drops anything no longer in view -- deleted, or filtered out -- so a
    // bulk action can only ever touch what the user can currently see.
    case selectionConstants.PRUNE: {
      const keep = {};
      for (const id of action.validIds || []) keep[id] = true;
      const next = state.ids.filter((id) => keep[id]);
      if (next.length === state.ids.length) return state;
      return {
        ...withIds(state, next),
        anchor: keep[state.anchor] ? state.anchor : null,
      };
    }

    case selectionConstants.RECORD_UNDO:
      return { ...state, undo: action.undo || null };

    case selectionConstants.CLEAR_UNDO:
      return { ...state, undo: null };

    default:
      return state;
  }
}
